import { useEffect, useRef } from "react";
import { Drawer } from "vaul";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { ProductImage } from "./ProductImage";
import { StockAdjustmentForm } from "./StockAdjustmentForm";
import { StockHistoryList } from "./StockHistoryList";
import type { Product } from "@/lib/validation/product";

interface StockDrawerProps {
  readonly product: Product | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Right-side drawer (bottom sheet on mobile via vaul) for stock adjustments.
 * Shows product header, current stock, adjustment form, and inline history.
 * Escape closes; focus is restored to the trigger element by vaul.
 */
export function StockDrawer({ product, open, onOpenChange }: StockDrawerProps) {
  const { t } = useTranslation("admin");
  const contentRef = useRef<HTMLDivElement>(null);

  // Stock version may be added server-side in later slice; fall back to 0.
  const stockVersion = (product as unknown as { stock_version?: number } | null)
    ?.stock_version ?? 0;

  // Focus first input on open for keyboard users
  useEffect(() => {
    if (!open || !contentRef.current) return;
    const timer = window.setTimeout(() => {
      const firstInput = contentRef.current?.querySelector<HTMLElement>(
        'input:not([type="radio"]), select, textarea, button',
      );
      firstInput?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (!product) return null;

  return (
    <Drawer.Root
      open={open}
      onOpenChange={onOpenChange}
      direction="right"
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Drawer.Content
          ref={contentRef}
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col bg-white shadow-xl outline-none md:rounded-l-xl"
          aria-label={t("products.stock.drawer_title")}
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-stone-200 px-6 py-4">
            <ProductImage
              src={product.image_url}
              alt={product.name}
              className="h-10 w-10 rounded-md"
              size="md"
            />
            <div className="min-w-0 flex-1">
              <Drawer.Title className="truncate text-base font-semibold text-stone-900">
                {product.name}
              </Drawer.Title>
              <Drawer.Description className="text-xs text-stone-500">
                {product.sku} &middot; {product.brand}
              </Drawer.Description>
            </div>
            <Drawer.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.close", { defaultValue: "Fermer" })}
              >
                <X className="h-4 w-4" />
              </Button>
            </Drawer.Close>
          </div>

          {/* Stat row — current stock with aria-live for screen-reader updates */}
          <div
            className="flex items-baseline gap-6 border-b border-stone-100 px-6 py-3"
            aria-live="polite"
            aria-atomic="true"
          >
            <div>
              <span className="text-2xl font-bold tabular-nums text-stone-900">
                {product.stock.toLocaleString("fr-SN")}
              </span>
              <span className="ml-2 text-xs text-stone-500">
                {t("products.stock.current_stock")}
              </span>
            </div>
          </div>

          {/* Adjustment form */}
          <div className="border-b border-stone-100 px-6 py-4">
            <StockAdjustmentForm
              productId={product.id}
              currentStock={product.stock}
              currentVersion={stockVersion}
            />
          </div>

          {/* History (scrollable) */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <StockHistoryList productId={product.id} />
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
